import anime from 'animejs/lib/anime.es.js'
import { LOAD_TIME } from '../lib/Constants'
import handleStats from './ComponentStats'

type TSections = (typeof sectionNames)[number]

interface IObserverOptions {
  root: Element | Document | null
  rootMargin: string
  threshold: number | number[]
}

const sectionNames = ['hero', 'offer', 'offer-2', 'stats', 'team'] as const

export const DURATIONS = {
  slowest: 5000,
  slower: 2500,
  slow: 2000,
  mid: 1500,
  fast: 1000,
  fastest: 500,
  initial: 200,
}

const EASING = {
  ease: 'cubicBezier(0.25, 1, 0.5, 1)',
}

window.addEventListener('load', () => {
  const matchMedia = window.matchMedia('(max-width: 992px)')

  const handleAnimations = () => {
    if (!matchMedia.matches) {
      const sections = document.querySelectorAll('[data-section]')

      sections.forEach((section) => {
        const animations = section.querySelectorAll(
          '[data-anim]',
        ) as unknown as HTMLElement[]

        if (animations.length > 0) {
          animations.forEach((anim) => (anim.style.opacity = '0'))
        }
      })

      return setTimeout(() => {
        const observeEl = (
          el: Element,
          fn: (entry: IntersectionObserverEntry) => void,
          options?: IObserverOptions,
        ) => {
          if ('IntersectionObserver' in window) {
            const observerOptions: IObserverOptions = options ?? {
              root: null,
              rootMargin: '0%',
              threshold: 0.4,
            }

            const lazyObserver = new IntersectionObserver((entries) => {
              entries.forEach((entry) => {
                if (entry.intersectionRatio > 0) {
                  fn(entry)

                  lazyObserver.unobserve(entry.target)
                }
              })
            }, observerOptions)

            lazyObserver.observe(el)
          }
        }

        const isSectionNameValid = (name: string): name is TSections => {
          if (sectionNames.some((n) => name === n)) {
            return true
          }

          return false
        }

        const animateSection = (
          sectionName: TSections,
          entry: IntersectionObserverEntry,
        ) => {
          const target = (str: string) => {
            return `[data-section="${sectionName}"] ${str}`
          }

          /* HERO */
          if (sectionName === 'hero') {
            anime({
              targets: target('[data-anim="circle"]'),
              translateY: ['-50%', '-50%'],
              opacity: [0, 1],
              transformOrigin: '0% 50%',
              easing: EASING.ease,
              duration: 2500,
              delay: 2000,
            })

            anime({
              targets: target('[data-anim="bg"]'),
              width: ['0%', '100%'],
              transformOrigin: '0% 50%',
              easing: EASING.ease,
              duration: 2500,
              delay: 100,
            })

            anime({
              targets: target('[data-anim="title"]'),
              translateX: ['400%', '0%'],
              translateY: ['-50%', '-50%'],
              opacity: [0, 1],
              easing: EASING.ease,
              duration: 2000,
              delay: 2000,
            })

            anime({
              targets: target('[data-anim="title-2"]'),
              translateX: ['400%', '0%'],
              translateY: ['-50%', '-50%'],
              opacity: [0, 1],
              color: ['#fff', '#000'],
              easing: EASING.ease,
              duration: 2000,
              delay: 2000,
            })

            anime({
              targets: target('[data-anim="quote"]'),
              keyframes: [
                { clipPath: 'inset(0 0 0 100%)' },
                { clipPath: 'inset(0 0 0 0%)' },
              ],
              easing: EASING.ease,
              duration: 2000,
              delay: 2000,
            })

            anime({
              targets: target('[data-anim="quote-text"]'),
              translateY: [-50, 0],
              opacity: [0, 1],
              easing: EASING.ease,
              duration: 1500,
              delay: 4000,
            })
          }

          /* OFFER */
          if (sectionName === 'offer') {
            anime({
              targets: target('[data-anim="bg"]'),
              translateX: [-1000, 0],
              opacity: [0, 1],
              easing: EASING.ease,
              duration: 1500,
              delay: 100,
            })

            anime({
              targets: target('[data-anim="text"]'),
              translateY: [-100, 0],
              opacity: [0, 1],
              easing: EASING.ease,
              duration: 2000,
              delay: 1600,
            })

            anime({
              targets: target('[data-anim="text-2"]'),
              translateY: [-100, 0],
              opacity: [0, 1],
              easing: EASING.ease,
              duration: 2000,
              delay: 1700,
            })

            anime({
              targets: target('[data-anim="more"]'),
              translateY: [-100, 0],
              opacity: [0, 1],
              easing: EASING.ease,
              duration: 2000,
              delay: 1800,
            })

            setTimeout(() => {
              anime({
                targets: target('[data-anim="benefit"]'),
                translateY: [-100, 0],
                opacity: [0, 1],
                easing: EASING.ease,
                duration: 2000,
                delay: anime.stagger(100),
              })
            }, 1800)
          }

          /* OFFER 2 */
          if (sectionName === 'offer-2') {
            anime({
              targets: target('[data-anim="bg"]'),
              translateX: [1000, 0],
              opacity: [0, 1],
              easing: EASING.ease,
              duration: 1500,
              delay: 100,
            })

            anime({
              targets: target('[data-anim="text"]'),
              translateY: [-100, 0],
              opacity: [0, 1],
              easing: EASING.ease,
              duration: 2000,
              delay: 1600,
            })

            anime({
              targets: target('[data-anim="text-2"]'),
              translateY: [-100, 0],
              opacity: [0, 1],
              easing: EASING.ease,
              duration: 2000,
              delay: 1700,
            })

            anime({
              targets: target('[data-anim="more"]'),
              translateY: [-100, 0],
              opacity: [0, 1],
              easing: EASING.ease,
              duration: 2000,
              delay: 1800,
            })

            setTimeout(() => {
              anime({
                targets: target('[data-anim="benefit"]'),
                translateY: [-100, 0],
                opacity: [0, 1],
                easing: EASING.ease,
                duration: 2000,
                delay: anime.stagger(100),
              })
            }, 1800)
          }

          /* STATS */
          if (sectionName === 'stats') {
            anime({
              targets: target('[data-anim="line"]'),
              width: ['0', '100%'],
              opacity: [0, 1],
              easing: EASING.ease,
              duration: 2000,
              delay: 100,
            })

            setTimeout(() => {
              anime({
                targets: target('[data-anim="icon"]'),
                translateY: [100, 0],
                opacity: [0, 0.15],
                easing: EASING.ease,
                duration: 2000,
                delay: anime.stagger(100),
              })
            }, 600)

            setTimeout(() => {
              anime({
                targets: target('[data-anim="text"]'),
                translateY: [100, 0],
                opacity: [0, 1],
                easing: EASING.ease,
                duration: 2000,
                delay: anime.stagger(100),
              })
            }, 700)

            setTimeout(() => {
              anime({
                targets: target('[data-anim="stat"]'),
                translateY: [100, 0],
                opacity: [0, 1],
                easing: EASING.ease,
                duration: 2000,
                delay: anime.stagger(100),
              })
            }, 800)

            if (!matchMedia.matches) {
              setTimeout(() => handleStats(), 800)
            }
          }

          /* TEAM */
          if (sectionName === 'team') {
            anime({
              targets: target('[data-anim="line"]'),
              translateX: [-300, 0],
              easing: EASING.ease,
              duration: 1500,
              delay: 100,
            })

            anime({
              targets: target('[data-anim="person-img"]'),
              keyframes: [
                { clipPath: 'inset(0 100% 0 0)' },
                { clipPath: 'inset(0 0% 0 0)' },
              ],
              easing: EASING.ease,
              duration: 2000,
              delay: 300,
            })

            anime({
              targets: target('[data-anim="person-title"]'),
              translateY: [-100, 0],
              opacity: [0, 1],
              easing: EASING.ease,
              duration: 2000,
              delay: 1200,
            })

            anime({
              targets: target('[data-anim="person-text"]'),
              translateY: [-100, 0],
              opacity: [0, 1],
              easing: EASING.ease,
              duration: 2000,
              delay: 1300,
            })

            anime({
              targets: target('[data-anim="person-more"]'),
              translateY: [-100, 0],
              opacity: [0, 1],
              easing: EASING.ease,
              duration: 2000,
              delay: 1400,
            })

            anime({
              targets: target('[data-anim="person-img-2"]'),
              keyframes: [
                { clipPath: 'inset(0 0 0 100%)' },
                { clipPath: 'inset(0 0 0 0%)' },
              ],
              easing: EASING.ease,
              duration: 2000,
              delay: 2200,
            })

            anime({
              targets: target('[data-anim="person-title-2"]'),
              translateY: [-100, 0],
              opacity: [0, 1],
              easing: EASING.ease,
              duration: 2000,
              delay: 3200,
            })

            anime({
              targets: target('[data-anim="person-text-2"]'),
              translateY: [-100, 0],
              opacity: [0, 1],
              easing: EASING.ease,
              duration: 2000,
              delay: 3300,
            })

            anime({
              targets: target('[data-anim="person-more-2"]'),
              translateY: [-100, 0],
              opacity: [0, 1],
              easing: EASING.ease,
              duration: 2000,
              delay: 3400,
            })

            anime({
              targets: target('[data-anim="team-title"]'),
              translateY: [-100, 0],
              opacity: [0, 1],
              easing: EASING.ease,
              duration: 2000,
              delay: 4200,
            })

            anime({
              targets: target('[data-anim="team-text"]'),
              translateY: [-100, 0],
              opacity: [0, 1],
              easing: EASING.ease,
              duration: 2000,
              delay: 4300,
            })

            anime({
              targets: target('[data-anim="team-more"]'),
              translateY: [-100, 0],
              opacity: [0, 1],
              easing: EASING.ease,
              duration: 2000,
              delay: 4400,
            })
          }
        }

        const animateEl = (
          elName: string,
          entry: IntersectionObserverEntry,
        ) => {
          if (elName === 'offers') {
            anime({
              targets: '[data-anim-single="offers"] [data-id="offer"]',
              translateY: [400, 0],
              opacity: [0, 1],
              easing: EASING.ease,
              duration: 600,
              delay: anime.stagger(400),
            })
          }
        }

        sections.forEach((section) => {
          const name = section.getAttribute('data-section')

          if (!name || !isSectionNameValid(name)) return

          observeEl(section, (entry) => {
            const animations = entry.target.querySelectorAll(
              '[data-anim]',
            ) as unknown as HTMLElement[]

            /*             if (animations.length > 0) {
              animations.forEach((anim) => (anim.style.opacity = '1'))
            } */

            animateSection(name, entry)
          })
        })

        const elements = document.querySelectorAll('[data-anim-single]')

        if (elements.length === 0) return

        elements.forEach((el) =>
          observeEl(el, (entry) => {
            const name = el.getAttribute('data-anim-single')

            if (typeof name !== 'string') return

            animateEl(name, entry)
          }),
        )
      }, LOAD_TIME)
    }

    // remove elements on mobile
    /*     const sections = document.querySelectorAll('[data-section]')

    sections.forEach((section) => {
      const animations = section.querySelectorAll(
        '[data-anim]',
      ) as unknown as HTMLElement[]

      animations.forEach((animation) => anime.remove(animation))
    }) */
  }

  // init animations
  matchMedia.addEventListener('change', handleAnimations)

  handleAnimations()
})
